import { EmailTemplate, EmailTemplateUpdate, TemplateVariable, RenderedEmail } from '../models/EmailTemplate';
import { PBTApiEndpoints } from './PBTConstants';

/** Error shape surfaced to sagas so the UI can show a message + correlation id. */
export interface EmailTemplateApiError {
  message: string;
  correlationId?: string;
}

/** Splits a comma-separated recipient string (backend shape) into an array (UI shape). */
function toRecipientArray(value?: string | null): string[] {
  if (!value) {
    return [];
  }
  return value
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

/** Joins UI recipient roles back into the comma-separated string the backend expects. */
function toRecipientString(value?: string[] | null): string {
  return (value ?? []).map((part) => part.trim()).filter(Boolean).join(',');
}

/** Maps a backend EmailTemplateResponse (camelCase JSON) onto the UI EmailTemplate model. */
function mapTemplate(dto: Record<string, unknown>): EmailTemplate {
  return {
    templateCode: (dto.templateCode as string) ?? '',
    templateName: (dto.templateName as string) ?? '',
    recipientType: ((dto.recipientType as string) ?? 'Manager') as EmailTemplate['recipientType'],
    triggerEvent: (dto.triggerEvent as string) ?? '',
    suppressionRule: (dto.suppressionRule as string) ?? '',
    hasAttachments: Boolean(dto.hasAttachments),
    isActive: Boolean(dto.isActive),
    emailSubject: (dto.emailSubject as string) ?? '',
    emailBody: (dto.emailBody as string) ?? '',
    ccRecipients: toRecipientArray(dto.ccRecipients as string | null),
    bccRecipients: toRecipientArray(dto.bccRecipients as string | null),
    lastModifiedDate: (dto.modifiedDate as string) ?? '',
    lastModifiedBy: (dto.modifiedBy as string) ?? '',
  };
}

/** Maps a backend TemplateVariableResponse onto the UI TemplateVariable model. */
function mapVariable(dto: Record<string, unknown>): TemplateVariable {
  return {
    name: (dto.name as string) ?? '',
    description: (dto.description as string) ?? '',
    example: (dto.sampleValue as string) ?? '',
  };
}

/** Maps a backend EmailTemplatePreviewResponse onto the UI RenderedEmail model. */
function mapPreview(dto: Record<string, unknown>): RenderedEmail {
  return {
    subject: (dto.renderedSubject as string) ?? '',
    body: (dto.renderedBody as string) ?? '',
    to: (dto.recipientType as string) ?? '',
    cc: toRecipientArray(dto.ccRecipients as string | null),
    bcc: toRecipientArray(dto.bccRecipients as string | null),
  };
}

/** Parses the PmResponse envelope, throwing an EmailTemplateApiError on failure. */
async function parseEnvelope<T>(response: Response, requireData = false): Promise<T> {
  const headerCorrelationId = response.headers.get('x-correlation-id') || undefined;
  let json: Record<string, unknown> | undefined;
  try {
    json = (await response.json()) as Record<string, unknown>;
  } catch {
    json = undefined;
  }

  const correlationId = (json?.correlationId as string) || headerCorrelationId;

  if (!response.ok) {
    const error = json?.error as { message?: string } | undefined;
    const message = error?.message || (json?.message as string) || `Request failed with status ${response.status}`;
    const apiError: EmailTemplateApiError = { message, correlationId };
    throw apiError;
  }

  const payload = (json?.data ?? json?.Data) as T | null | undefined;
  if (requireData && payload == null) {
    throw {
      message: 'Invalid response envelope: missing data.',
      correlationId,
    } as EmailTemplateApiError;
  }

  return payload as T;
}

async function request(url: string, token: string, init: RequestInit, timeoutMs = 15000): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, {
      ...init,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...(init.headers ?? {}),
      },
      signal: controller.signal,
    });
  } catch (err) {
    if ((err as { name?: string })?.name === 'AbortError') {
      throw { message: `Request timed out after ${timeoutMs}ms` } as EmailTemplateApiError;
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

/** GET /api/v1/pbt/email-templates */
export async function fetchEmailTemplatesApi(token: string): Promise<EmailTemplate[]> {
  const response = await request(PBTApiEndpoints.EmailTemplates, token, { method: 'GET' });
  const data = await parseEnvelope<Record<string, unknown>[]>(response);
  return (data ?? []).map(mapTemplate);
}

/** GET /api/v1/pbt/email-templates/variables */
export async function fetchTemplateVariablesApi(token: string): Promise<TemplateVariable[]> {
  const response = await request(PBTApiEndpoints.EmailTemplateVariables, token, { method: 'GET' });
  const data = await parseEnvelope<Record<string, unknown>[]>(response);
  return (data ?? []).map(mapVariable);
}

/** PUT /api/v1/pbt/email-templates/{code} */
export async function updateEmailTemplateApi(
  token: string,
  code: string,
  data: EmailTemplateUpdate
): Promise<EmailTemplate> {
  const body = {
    emailSubject: data.emailSubject,
    emailBody: data.emailBody,
    ccRecipients: toRecipientString(data.ccRecipients),
    bccRecipients: toRecipientString(data.bccRecipients),
    isActive: data.isActive,
  };
  const response = await request(PBTApiEndpoints.EmailTemplateByCode(code), token, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
  const updated = await parseEnvelope<Record<string, unknown>>(response, true);
  return mapTemplate(updated);
}

/** POST /api/v1/pbt/email-templates/{code}/preview */
export async function previewEmailTemplateApi(token: string, code: string): Promise<RenderedEmail> {
  const response = await request(PBTApiEndpoints.EmailTemplatePreview(code), token, {
    method: 'POST',
    body: JSON.stringify({}),
  });
  const preview = await parseEnvelope<Record<string, unknown>>(response, true);
  return mapPreview(preview);
}
